import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useAgreeTermsForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealOpportunityLimitedPartnerViewV3QueryKey } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestCommitmentAttempted } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseAcceptAllocationProps = {
  dealOpportunityId: string;
};

export const useAcceptAllocation = ({
  dealOpportunityId,
}: UseAcceptAllocationProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: acceptAllocation, isPending: isAcceptingAllocation } =
    useAgreeTermsForDealOpportunity({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.status_success(),
            description: m.coInvestments_allocation_accept_successMessage(),
          });

          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityLimitedPartnerViewV3QueryKey(
                dealOpportunityId,
              ),
          });
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_allocation_accept_errorMessage(),
          });
        },
      },
    });

  const handleAcceptAllocation = useCallback(() => {
    trackCoInvestCommitmentAttempted({ dealOpportunityId });
    acceptAllocation({
      dealOpportunityId,
    });
  }, [dealOpportunityId, acceptAllocation]);

  return {
    handleAcceptAllocation,
    isAcceptingAllocation,
  };
};
